/**
 * 图片格式兼容工具：按魔数识别真实格式，并将 HEIF/HEIC 转为可预览的 JPEG。
 * 用于解决服务端 content-type 标为 jpeg、实际却是 HEIF 导致无法预览的问题。
 */

/** HEIF 容器常见 brand（ftyp 后 4 字节） */
const HEIF_BRANDS = new Set([
  'heic',
  'heix',
  'hevc',
  'hevx',
  'heim',
  'heis',
  'hevm',
  'hevs',
  'mif1',
  'msf1',
]);

/**
 * 从二进制头读取 ASCII 片段。
 */
function readAscii(bytes: Uint8Array, start: number, length: number): string {
  let result = '';
  const end = Math.min(start + length, bytes.length);
  for (let i = start; i < end; i += 1) {
    result += String.fromCharCode(bytes[i]);
  }
  return result;
}

/**
 * 根据文件魔数推断 MIME 类型；无法识别时返回 null。
 */
export function detectImageMimeType(buffer: ArrayBuffer): string | null {
  const bytes = new Uint8Array(buffer);
  if (bytes.length < 12) {
    return null;
  }

  // JPEG: FF D8 FF
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }

  // PNG: 89 50 4E 47 0D 0A 1A 0A
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return 'image/png';
  }

  // GIF
  if (readAscii(bytes, 0, 4) === 'GIF8') {
    return 'image/gif';
  }

  // WEBP: RIFF....WEBP
  if (readAscii(bytes, 0, 4) === 'RIFF' && readAscii(bytes, 8, 4) === 'WEBP') {
    return 'image/webp';
  }

  // BMP
  if (bytes[0] === 0x42 && bytes[1] === 0x4d) {
    return 'image/bmp';
  }

  // HEIF/HEIC：ISO BMFF，offset 4 为 ftyp，offset 8 起为 brand
  if (readAscii(bytes, 4, 4) === 'ftyp') {
    const brand = readAscii(bytes, 8, 4).toLowerCase();
    if (HEIF_BRANDS.has(brand)) {
      return 'image/heic';
    }
    // 兼容 brand 出现在 compatible brands 列表中的情况
    const headerText = readAscii(bytes, 8, Math.min(bytes.length - 8, 32)).toLowerCase();
    const heifBrandList = Array.from(HEIF_BRANDS);
    for (let i = 0; i < heifBrandList.length; i += 1) {
      if (headerText.includes(heifBrandList[i])) {
        return 'image/heic';
      }
    }
  }

  return null;
}

/**
 * 判断是否为 HEIF/HEIC。
 */
export function isHeifImage(buffer: ArrayBuffer): boolean {
  const mime = detectImageMimeType(buffer);
  return mime === 'image/heic';
}

/**
 * 将 ArrayBuffer 转为 base64 字符串。
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
    let chunkBinary = '';
    for (let j = 0; j < chunk.length; j += 1) {
      chunkBinary += String.fromCharCode(chunk[j]);
    }
    binary += chunkBinary;
  }
  return btoa(binary);
}

/**
 * 将 Blob 转为 data URL。
 */
async function blobToDataUrl(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const mime = blob.type || 'image/jpeg';
  return `data:${mime};base64,${arrayBufferToBase64(buffer)}`;
}

/**
 * 规范化 MIME：去掉 charset 等参数，仅保留主类型。
 */
function normalizeMimeType(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  const mime = value.split(';')[0].trim().toLowerCase();
  return mime || null;
}

/**
 * 将认证图片二进制转为浏览器可预览的 data URL。
 * - 按魔数识别真实格式，避免误用 content-type / 硬编码 jpeg
 * - HEIF/HEIC 懒加载 heic2any 转为 JPEG
 */
export async function toPreviewableImageDataUrl(
  buffer: ArrayBuffer,
  fallbackMimeType?: string | null,
): Promise<string> {
  const detectedMime = detectImageMimeType(buffer);
  const mimeType = detectedMime || normalizeMimeType(fallbackMimeType) || 'image/jpeg';

  if (mimeType === 'image/heic' || mimeType === 'image/heif') {
    try {
      const { default: heic2any } = await import('heic2any');
      const converted = await heic2any({
        blob: new Blob([new Uint8Array(buffer)], { type: 'image/heic' }),
        toType: 'image/jpeg',
        quality: 0.92,
      });
      const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
      return blobToDataUrl(jpegBlob);
    } catch (error) {
      console.error('HEIF 转 JPEG 失败，回退原始数据:', error);
      // 回退：仍用正确 MIME，部分 Safari 可能可显示
      return `data:image/heic;base64,${arrayBufferToBase64(buffer)}`;
    }
  }

  return `data:${mimeType};base64,${arrayBufferToBase64(buffer)}`;
}
